import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Spinner } from '../../components/Spinner'
import { useDownloadReport, useReports } from '../../hooks/useReports'
import { useTableUrlState } from '../../hooks/useTableUrlState'
import Pagination from '../../components/admin/Pagination'
import {
  formatDateInTimezone,
  formatMonthInTimezone,
  formatVolume,
} from '../../utils/formatInTimezone'
import { getUnitAbbreviation } from '../../utils/unitConversionUtils'
import { UnitCategoryId } from '../../common/enums'
import type { PortalReportSummary } from '../../types/flowBilling'

/**
 * Reports — the pull half of report delivery.
 *
 * Reports are emailed, but an email is a moment: it gets deleted, or it
 * predates the person now doing the reconciling. This page is how somebody
 * finds March without asking anyone.
 *
 * Scoping is entirely server-side. A user sees the groups they belong to; an
 * administrator sees all of them. Nothing here filters by permission, because a
 * client-side filter over a full list would still have sent the full list.
 */

const PAGE_SIZE = 25

/** A superseded run is a report that was corrected — still real, still readable. */
function StatusBadge({ report }: { report: PortalReportSummary }) {
  if (report.status === 'superseded') {
    return (
      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs bg-gray-100 text-gray-600">
        superseded
      </span>
    )
  }
  if (report.revisionOf) {
    return (
      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs bg-amber-100 text-amber-800">
        revised
      </span>
    )
  }
  return null
}

export default function ReportsListPage() {
  const { page, setPage, getFilter, setFilter } = useTableUrlState({ defaultPageSize: PAGE_SIZE })
  const yearFilter = getFilter('year')
  const year = yearFilter ? Number(yearFilter) : undefined

  const { data, isLoading, isError, error } = useReports({ page, limit: PAGE_SIZE, year })
  const download = useDownloadReport()

  const years = useMemo(() => {
    const now = new Date().getFullYear()
    return [now, now - 1, now - 2, now - 3]
  }, [])

  const reports = data?.reports ?? []

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600 mt-1">
            Every report generated for the groups you have access to.
          </p>
        </div>
        {/* The Report Panel carries this link now; a second copy in the corner
            would be two ways to the same page on the same screen. */}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-gray-600" htmlFor="report-year">
          Year
        </label>
        <select
          id="report-year"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          value={yearFilter ?? ''}
          onChange={(e) => setFilter('year', e.target.value)}
        >
          <option value="">All</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* A line of grey text is easy to mistake for an empty list, which is
          exactly the wrong thing to conclude about a billing history. */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Spinner size={36} color="#9ca3af" />
          <span className="text-sm text-gray-500">Loading reports…</span>
        </div>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {(error as any)?.response?.data?.error?.message || 'Could not load reports.'}
        </div>
      )}

      {!isLoading && !isError && reports.length === 0 && (
        // Deliberately an explanation rather than a hidden menu item: a user
        // with no reported group should learn that reports exist and that they
        // have none, not silently lose the section.
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">No reports yet</p>
          <p className="text-sm text-gray-600 mt-1">
            No reports have been generated for your groups. They appear here once the first one is
            issued.
          </p>
        </div>
      )}

      {reports.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                    Period
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                    Group
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">
                    Total
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                    Issued
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report) => {
                  const unit = getUnitAbbreviation(UnitCategoryId.Volume, report.unitId)
                  return (
                    <tr key={report.id} className="hover:bg-blue-50">
                      <td className="px-4 py-3 text-sm">
                        {/* Badge beside the month, not beneath it: a wrapper
                            that renders whether or not there is a badge makes
                            every row in the table a different height. */}
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/reports/${report.id}`}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            {formatMonthInTimezone(report.periodStart, report.timezone)}
                          </Link>
                          <StatusBadge report={report} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{report.groupName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                        {formatVolume(report.totalVolume, report.decimalPlaces)} {unit}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {/* A report published without an email has no sentAt;
                            the date it was produced is what "issued" means for
                            it, and showing a dash would read as "never". */}
                        {report.sentAt || report.generatedAt
                          ? formatDateInTimezone((report.sentAt || report.generatedAt)!, report.timezone)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                        <Link to={`/reports/${report.id}`} className="text-blue-600 hover:underline">
                          View
                        </Link>
                        <button
                          type="button"
                          className="ml-3 text-blue-600 hover:underline disabled:text-gray-400"
                          disabled={download.isPending}
                          onClick={() => download.mutate({ id: report.id, format: 'csv' })}
                        >
                          CSV
                        </button>
                        <button
                          type="button"
                          className="ml-3 text-blue-600 hover:underline disabled:text-gray-400"
                          disabled={download.isPending}
                          onClick={() => download.mutate({ id: report.id, format: 'pdf' })}
                        >
                          PDF
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {data && data.pagination.pages > 1 && (
            <Pagination
              currentPage={data.pagination.page}
              totalPages={data.pagination.pages}
              totalItems={data.pagination.total}
              onPageChange={setPage}
            />
          )}
        </div>
      )}
    </div>
  )
}
